import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Truck, Package, Zap, CreditCard, CheckCircle2, MapPin, Building, Smartphone, Copy, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'IN', name: 'India' }
];

const CheckoutPage = () => {
  const { cart, fetchCart, clearCart } = useCart();
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Address, 2: Shipping Method, 3: Payment Method, 4: Review
  const [loading, setLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [freeShippingInfo, setFreeShippingInfo] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'manual'
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
  
  const [address, setAddress] = useState({
    full_name: user?.name || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: ''
  });

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/cart');
    }
    // Fetch payment info
    fetchPaymentInfo();
  }, [cart, navigate]);

  const fetchPaymentInfo = async () => {
    setLoadingPaymentInfo(true);
    try {
      const response = await axios.get(`${API_URL}/payment-info`);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
    } finally {
      setLoadingPaymentInfo(false);
    }
  };

  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const validateAddress = () => {
    const required = ['full_name', 'address_line1', 'city', 'state', 'postal_code', 'country'];
    for (const field of required) {
      if (!address[field] || address[field].trim() === '') {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    return true;
  };

  const calculateShipping = async () => {
    if (!validateAddress()) return;
    
    setCalculatingShipping(true);
    try {
      const response = await axios.post(`${API_URL}/shipping/calculate`, {
        postal_code: address.postal_code,
        country: address.country,
        subtotal: cart.total
      });
      
      setShippingOptions(response.data.options);
      setFreeShippingInfo({
        available: response.data.free_shipping_available,
        threshold: response.data.free_shipping_threshold,
        amountToFree: response.data.amount_to_free_shipping
      });
      setStep(2);
    } catch (error) {
      console.error('Error calculating shipping:', error);
      toast.error('Failed to calculate shipping');
    } finally {
      setCalculatingShipping(false);
    }
  };

  const getSelectedShippingCost = () => {
    const selected = shippingOptions.find(opt => opt.id === selectedShipping);
    return selected ? selected.price : 0;
  };

  const getTotal = () => {
    return cart.total + getSelectedShippingCost();
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API_URL}/orders`, {
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        shipping_address: address,
        shipping_method: selectedShipping,
        payment_method: paymentMethod
      }, getAuthHeaders());

      if (paymentMethod === 'card') {
        // Create checkout session for Stripe
        const checkoutResponse = await axios.post(`${API_URL}/payments/checkout`, {
          order_id: orderResponse.data.order_id,
          origin_url: window.location.origin
        }, getAuthHeaders());

        // Redirect to Stripe checkout
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        // Manual payment - redirect to success with instructions
        await clearCart();
        navigate('/checkout/success', { 
          state: { 
            orderId: orderResponse.data.order_id,
            paymentMethod: 'manual',
            paymentInfo: paymentInfo,
            total: getTotal()
          }
        });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getShippingIcon = (methodId) => {
    switch (methodId) {
      case 'overnight': return <Zap className="w-5 h-5" />;
      case 'express': return <Package className="w-5 h-5" />;
      default: return <Truck className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => step === 1 ? navigate('/cart') : setStep(step - 1)}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            {step === 1 ? 'Back to Cart' : 'Back'}
          </Button>
          <h1 className="font-heading text-3xl font-bold text-royal-700">Checkout</h1>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 px-4">
          <div className="flex items-center gap-2 sm:gap-4">
            {[
              { num: 1, label: 'Address' },
              { num: 2, label: 'Shipping' },
              { num: 3, label: 'Payment' },
              { num: 4, label: 'Review' }
            ].map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-saffron-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > s.num ? 'bg-saffron-600 text-white' : 
                    step === s.num ? 'bg-saffron-100 text-saffron-600 border-2 border-saffron-600' : 
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {step > s.num ? <CheckCircle2 className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="hidden sm:inline font-medium">{s.label}</span>
                </div>
                {index < 3 && (
                  <div className={`w-8 sm:w-12 h-0.5 mx-2 ${step > s.num ? 'bg-saffron-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Shipping Address */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-saffron-600" />
                    Shipping Address
                  </CardTitle>
                  <CardDescription>Enter your delivery address</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={address.full_name}
                        onChange={(e) => handleAddressChange('full_name', e.target.value)}
                        placeholder="John Singh"
                        data-testid="input-full-name"
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <Label htmlFor="address_line1">Address Line 1 *</Label>
                      <Input
                        id="address_line1"
                        value={address.address_line1}
                        onChange={(e) => handleAddressChange('address_line1', e.target.value)}
                        placeholder="123 Main Street"
                        data-testid="input-address1"
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                      <Input
                        id="address_line2"
                        value={address.address_line2}
                        onChange={(e) => handleAddressChange('address_line2', e.target.value)}
                        placeholder="Apt, Suite, Unit, etc."
                        data-testid="input-address2"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={address.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        placeholder="New York"
                        data-testid="input-city"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Select
                        value={address.state}
                        onValueChange={(value) => handleAddressChange('state', value)}
                      >
                        <SelectTrigger data-testid="select-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map(state => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="postal_code">Postal Code *</Label>
                      <Input
                        id="postal_code"
                        value={address.postal_code}
                        onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                        placeholder="10001"
                        data-testid="input-postal-code"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Select
                        value={address.country}
                        onValueChange={(value) => handleAddressChange('country', value)}
                      >
                        <SelectTrigger data-testid="select-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(country => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="sm:col-span-2">
                      <Label htmlFor="phone">Phone Number (Optional)</Label>
                      <Input
                        id="phone"
                        value={address.phone}
                        onChange={(e) => handleAddressChange('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full btn-primary mt-6" 
                    onClick={calculateShipping}
                    disabled={calculatingShipping}
                    data-testid="continue-to-shipping"
                  >
                    {calculatingShipping ? 'Calculating...' : 'Continue to Shipping'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Shipping Method */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-saffron-600" />
                    Shipping Method
                  </CardTitle>
                  <CardDescription>Choose your preferred delivery speed</CardDescription>
                </CardHeader>
                <CardContent>
                  {freeShippingInfo && !freeShippingInfo.available && freeShippingInfo.amountToFree > 0 && (
                    <div className="bg-saffron-50 border border-saffron-200 rounded-lg p-4 mb-6">
                      <p className="text-saffron-800 text-sm">
                        🎁 Add <span className="font-bold">${freeShippingInfo.amountToFree.toFixed(2)}</span> more to your cart 
                        for <span className="font-bold">FREE standard shipping!</span>
                      </p>
                    </div>
                  )}
                  
                  <RadioGroup value={selectedShipping} onValueChange={setSelectedShipping}>
                    <div className="space-y-3">
                      {shippingOptions.map((option) => (
                        <div
                          key={option.id}
                          className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedShipping === option.id 
                              ? 'border-saffron-500 bg-saffron-50' 
                              : 'border-gray-200 hover:border-saffron-200'
                          }`}
                          onClick={() => setSelectedShipping(option.id)}
                          data-testid={`shipping-option-${option.id}`}
                        >
                          <RadioGroupItem value={option.id} id={option.id} className="mr-4" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {getShippingIcon(option.id)}
                              <Label htmlFor={option.id} className="font-medium cursor-pointer">
                                {option.name}
                              </Label>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{option.estimated_days}</p>
                          </div>
                          <div className="text-right">
                            {option.free_shipping_applied ? (
                              <div>
                                <span className="text-green-600 font-bold">FREE</span>
                                <span className="text-gray-400 line-through text-sm ml-2">
                                  ${option.original_price?.toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <span className="font-bold text-gray-900">${option.price.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                  
                  <Button 
                    className="w-full btn-primary mt-6" 
                    onClick={() => setStep(3)}
                    data-testid="continue-to-payment"
                  >
                    Continue to Payment
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Payment Method */}
            {step === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-saffron-600" />
                    Payment Method
                  </CardTitle>
                  <CardDescription>Choose how you want to pay</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingPaymentInfo ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-saffron-500" />
                    </div>
                  ) : (
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="space-y-4">
                        {/* Credit Card Option */}
                        <div
                          className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            paymentMethod === 'card' 
                              ? 'border-saffron-500 bg-saffron-50' 
                              : 'border-gray-200 hover:border-saffron-200'
                          }`}
                          onClick={() => setPaymentMethod('card')}
                        >
                          <RadioGroupItem value="card" id="card" className="mr-4 mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-5 h-5 text-saffron-600" />
                              <Label htmlFor="card" className="font-medium cursor-pointer">
                                Pay with Card (Stripe)
                              </Label>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Secure payment via Stripe. Accepts all major credit/debit cards.
                            </p>
                          </div>
                        </div>

                        {/* Manual Payment Option */}
                        {paymentInfo?.has_manual_payment && (
                          <div
                            className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              paymentMethod === 'manual' 
                                ? 'border-saffron-500 bg-saffron-50' 
                                : 'border-gray-200 hover:border-saffron-200'
                            }`}
                            onClick={() => setPaymentMethod('manual')}
                          >
                            <RadioGroupItem value="manual" id="manual" className="mr-4 mt-1" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Building className="w-5 h-5 text-saffron-600" />
                                <Label htmlFor="manual" className="font-medium cursor-pointer">
                                  Manual Payment (Bank Transfer/Zelle/PayPal)
                                </Label>
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                Transfer money directly. Your order will be processed after payment confirmation.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </RadioGroup>
                  )}

                  {/* Show payment details preview for manual payment */}
                  {paymentMethod === 'manual' && paymentInfo?.has_manual_payment && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-800 mb-3">Payment Details (for reference)</h4>
                      <div className="space-y-2 text-sm text-blue-700">
                        {paymentInfo.bank_name && (
                          <p><span className="font-medium">Bank:</span> {paymentInfo.bank_name}</p>
                        )}
                        {paymentInfo.zelle_phone && (
                          <p><span className="font-medium">Zelle:</span> {paymentInfo.zelle_phone}</p>
                        )}
                        {paymentInfo.paypal_username && (
                          <p><span className="font-medium">PayPal:</span> {paymentInfo.paypal_username}</p>
                        )}
                        {paymentInfo.venmo_username && (
                          <p><span className="font-medium">Venmo:</span> {paymentInfo.venmo_username}</p>
                        )}
                      </div>
                      <p className="text-xs text-blue-600 mt-3">
                        Full payment details will be shown after placing order.
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    className="w-full btn-primary mt-6" 
                    onClick={() => setStep(4)}
                    data-testid="continue-to-review"
                  >
                    Continue to Review
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Review Order */}
            {step === 4 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-saffron-600" />
                    Review Your Order
                  </CardTitle>
                  <CardDescription>Confirm your order details before payment</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Shipping Address */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Shipping Address</h3>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm">
                      <p className="font-medium">{address.full_name}</p>
                      <p>{address.address_line1}</p>
                      {address.address_line2 && <p>{address.address_line2}</p>}
                      <p>{address.city}, {address.state} {address.postal_code}</p>
                      <p>{COUNTRIES.find(c => c.code === address.country)?.name}</p>
                      {address.phone && <p className="mt-1">Phone: {address.phone}</p>}
                    </div>
                    <Button variant="link" className="p-0 h-auto mt-2" onClick={() => setStep(1)}>
                      Edit Address
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Shipping Method */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Shipping Method</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {shippingOptions.length > 0 ? (
                        shippingOptions.filter(o => o.id === selectedShipping).map(option => (
                          <div key={option.id} className="flex items-center justify-between" data-testid="selected-shipping-method">
                            <div className="flex items-center gap-2">
                              {getShippingIcon(option.id)}
                              <div>
                                <p className="font-medium">{option.name}</p>
                                <p className="text-sm text-gray-500">{option.estimated_days}</p>
                              </div>
                            </div>
                            <span className={option.free_shipping_applied ? 'text-green-600 font-bold' : 'font-medium'}>
                            {option.free_shipping_applied ? 'FREE' : `$${option.price.toFixed(2)}`}
                          </span>
                        </div>
                      ))
                      ) : (
                        <div className="flex items-center justify-between" data-testid="selected-shipping-fallback">
                          <div className="flex items-center gap-2">
                            {getShippingIcon(selectedShipping)}
                            <div>
                              <p className="font-medium">{selectedShipping === 'standard' ? 'Standard Shipping' : selectedShipping === 'express' ? 'Express Shipping' : 'Overnight Shipping'}</p>
                              <p className="text-sm text-gray-500">{selectedShipping === 'standard' ? '5-7 business days' : selectedShipping === 'express' ? '2-3 business days' : '1 business day'}</p>
                            </div>
                          </div>
                          <span className="font-medium">Calculating...</span>
                        </div>
                      )}
                    </div>
                    <Button variant="link" className="p-0 h-auto mt-2" onClick={() => setStep(2)}>
                      Change Shipping Method
                    </Button>
                  </div>
                  
                  <Separator />

                  {/* Payment Method */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Payment Method</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        {paymentMethod === 'card' ? (
                          <>
                            <CreditCard className="w-5 h-5 text-saffron-600" />
                            <div>
                              <p className="font-medium">Credit/Debit Card</p>
                              <p className="text-sm text-gray-500">Pay securely via Stripe</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Building className="w-5 h-5 text-saffron-600" />
                            <div>
                              <p className="font-medium">Manual Payment</p>
                              <p className="text-sm text-gray-500">Bank Transfer / Zelle / PayPal / Venmo</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <Button variant="link" className="p-0 h-auto mt-2" onClick={() => setStep(3)}>
                      Change Payment Method
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Order Items */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Order Items ({cart.items.length})</h3>
                    <div className="space-y-3">
                      {cart.items.map((item) => (
                        <div key={item.product_id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                          <div className="w-16 h-16 bg-white rounded overflow-hidden flex-shrink-0">
                            <img
                              src={item.product?.image_url || `https://via.placeholder.com/64x64?text=${encodeURIComponent(item.product?.name || 'Product')}`}
                              alt={item.product?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{item.product?.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-medium">${item.item_total?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full btn-primary" 
                    onClick={handlePlaceOrder}
                    disabled={loading}
                    data-testid="place-order-button"
                  >
                    {loading ? (
                      'Processing...'
                    ) : paymentMethod === 'card' ? (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pay with Card - ${getTotal().toFixed(2)}
                      </>
                    ) : (
                      <>
                        <Building className="w-5 h-5 mr-2" />
                        Place Order - ${getTotal().toFixed(2)}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    {paymentMethod === 'card' 
                      ? 'You will be redirected to Stripe for secure payment'
                      : 'You will receive payment details after placing order'
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items Summary */}
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-gray-600 truncate max-w-[150px]">
                        {item.product?.name} × {item.quantity}
                      </span>
                      <span>${item.item_total?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${cart.total?.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  {step >= 2 ? (
                    <span className={getSelectedShippingCost() === 0 ? 'text-green-600 font-medium' : ''}>
                      {getSelectedShippingCost() === 0 ? 'FREE' : `$${getSelectedShippingCost().toFixed(2)}`}
                    </span>
                  ) : (
                    <span className="text-gray-400">Calculated next</span>
                  )}
                </div>
                
                <Separator />
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-saffron-600">
                    ${step >= 2 ? getTotal().toFixed(2) : cart.total?.toFixed(2)}
                  </span>
                </div>
                
                {freeShippingInfo && freeShippingInfo.available && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Free shipping applied!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
